import React, { useState, useEffect, useRef } from 'react';

const ITEM_HEIGHT = 32; // リールアイテムの高さをさらに小さく (iPhone SE対応)
const VISIBLE_ITEMS = 3; // 表示する項目数

const SlotReel = ({ spinning, symbols, onStop, targetIndex, highlightIndexes, reelIndex, isBlueGlow, coins, payout, speedFactor: speedFactorProp, gameCategory }) => {
  const [position, setPosition] = useState(0);
  const [reelWidth, setReelWidth] = useState(0);
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(null);
  const reelRef = useRef(null);
  const spinningRef = useRef(spinning);
  const canvasRef = useRef(null);
  const fontFamilyRef = useRef('');

  // リール幅（= 文字の収まり計算の基準）を追従
  useEffect(() => {
    const el = reelRef.current;
    if (!el) return;

    const update = () => {
      try {
        const rect = el.getBoundingClientRect();
        const w = Math.round(rect.width || 0);
        if (w > 0) setReelWidth(w);
      } catch (_) {}
      try {
        if (typeof window !== 'undefined' && window.getComputedStyle) {
          const ff = window.getComputedStyle(el).fontFamily;
          if (ff) fontFamilyRef.current = ff;
        }
      } catch (_) {}
    };

    update();

    let ro = null;
    try {
      if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(() => update());
        ro.observe(el);
      }
    } catch (_) { ro = null; }

    // フォールバック: ResizeObserver が無い/不安定な場合に備えて resize でも更新
    const onResize = () => update();
    try {
      window.addEventListener('resize', onResize);
      window.addEventListener('orientationchange', onResize);
    } catch (_) {}

    return () => {
      try { ro?.disconnect(); } catch (_) {}
      try {
        window.removeEventListener('resize', onResize);
        window.removeEventListener('orientationchange', onResize);
      } catch (_) {}
    };
  }, []);

  const measureTextWidth = React.useCallback((text, fontSizePx, fontWeight) => {
    try {
      const t = String(text ?? '');
      if (!t) return 0;
      if (!canvasRef.current) canvasRef.current = document.createElement('canvas');
      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) return 0;
      const ff = fontFamilyRef.current || (typeof window !== 'undefined' && window.getComputedStyle ? window.getComputedStyle(document.body).fontFamily : '') || 'system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif';
      ctx.font = `${fontWeight} ${fontSizePx}px ${ff}`;
      const metrics = ctx.measureText(t);
      return metrics.width || 0;
    } catch (_) {
      return 0;
    }
  }, []);

  // 1つでも縮小が必要なら、全コマンドを同じサイズに統一（=必要最小サイズ）
  const unifiedFontSizePx = React.useMemo(() => {
    const base = gameCategory === 'sql' ? 14 : 20;
    const min = gameCategory === 'sql' ? 10 : 12;
    const weight = gameCategory === 'sql' ? 700 : 800;
    const available = Math.max(10, (reelWidth || 0) - 12); // border/余白ぶん安全マージン
    if (!Array.isArray(symbols) || symbols.length === 0) return base;

    let requiredMin = base;
    for (const s of symbols) {
      const text = String(s?.text ?? '');
      if (!text) continue;
      const w = measureTextWidth(text, base, weight);
      if (w > 0 && w > available) {
        const scaled = Math.floor(base * (available / w));
        requiredMin = Math.min(requiredMin, Math.max(min, scaled));
      }
    }
    return requiredMin;
  }, [symbols, reelWidth, gameCategory, measureTextWidth]);

  // spinningの状態が変わったらrefを更新
  useEffect(() => {
    spinningRef.current = spinning;
  }, [spinning, reelIndex]);

  // アニメーションループ関数をuseCallbackでメモ化
  // スマホ最適化: 時間ベース更新で30fps固定
  const lastTimeRef = useRef(null);
  const accumulatorRef = useRef(0);
  const animate = React.useCallback(() => {
    if (spinningRef.current) {
      const now = performance.now();
      if (lastTimeRef.current == null) lastTimeRef.current = now;
      const dt = now - lastTimeRef.current;
      lastTimeRef.current = now;
      accumulatorRef.current += dt;
      const frameInterval = 1000 / 30;
      if (accumulatorRef.current >= frameInterval) {
        const steps = Math.floor(accumulatorRef.current / frameInterval);
        accumulatorRef.current -= steps * frameInterval;
        setPosition(prevPosition => {
          const speedFactor = typeof speedFactorProp === 'number' && speedFactorProp > 0 ? speedFactorProp : 4;
          const speedPerMs = speedFactor / (1000 / 60);
          const movePx = speedPerMs * (steps * frameInterval);
          const nextPosition = (prevPosition - movePx + symbols.length * ITEM_HEIGHT * 2) % (symbols.length * ITEM_HEIGHT);
          return nextPosition;
        });
      }
      animationFrameRef.current = requestAnimationFrame(animate);
    }
  }, [symbols.length, speedFactorProp]);

  // 正規化オフセット（連続回転中のみ使用）
  const totalHeight = symbols.length * ITEM_HEIGHT;
  const normalizedPos = ((position % totalHeight) + totalHeight) % totalHeight;
  const offset = -Math.round(normalizedPos);

  // スピン状態の変化を検知してアニメーション開始/停止
  useEffect(() => {
    if (spinning) {
      // スピン開始
      startTimeRef.current = Date.now();
      lastTimeRef.current = null;
      accumulatorRef.current = 0;
      
      // アニメーションスタート
      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    } else {
      // スピン停止
      
      // 停止処理
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
        
        // 即座に停止位置を計算
        let stopIndex;
        if (targetIndex !== null) {
          stopIndex = targetIndex;
        } else {
          const currentPos = position / ITEM_HEIGHT;
          const exactIndex = Math.floor(currentPos) % symbols.length;
          stopIndex = (exactIndex + 1) % symbols.length; // 1つ先のシンボルで停止
        }
        
        // 停止位置を設定（真ん中の絵柄の中心を中心線に合わせる）
        // targetIndexの絵柄が真ん中（2番目）の位置に来るように、1つ上にオフセット
        const targetPos = ((stopIndex - 1 + symbols.length) % symbols.length) * ITEM_HEIGHT;
        setPosition(targetPos);
        
        // 親コンポーネントに通知
        if (onStop) {
          onStop(stopIndex);
        }
      }
    }
    
    // クリーンアップ
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [spinning, targetIndex, position, symbols.length, onStop, reelIndex, animate]);

  // 色の視認性を確保するため暗い色は自動で明るく補正
  const getReadableColor = React.useCallback((hex) => {
    if (!hex || typeof hex !== 'string') return '#ffffff';
    let h = hex.trim();
    if (h[0] === '#') h = h.slice(1);
    if (h.length === 3) {
      h = h.split('').map(ch => ch + ch).join('');
    }
    if (h.length !== 6) return hex;
    const r = parseInt(h.slice(0,2), 16);
    const g = parseInt(h.slice(2,4), 16);
    const b = parseInt(h.slice(4,6), 16);
    // 簡易輝度（ガンマ未補正）：十分暗い色は白を混ぜて持ち上げる
    const brightness = 0.299 * r + 0.587 * g + 0.114 * b; // 0..255
    if (brightness >= 155) return `#${h}`; // すでに十分明るい
    const mix = 0.55; // 白の混合量（55%）
    const nr = Math.min(255, Math.round(r + (255 - r) * mix));
    const ng = Math.min(255, Math.round(g + (255 - g) * mix));
    const nb = Math.min(255, Math.round(b + (255 - b) * mix));
    const toHex = (n) => n.toString(16).padStart(2, '0');
    return `#${toHex(nr)}${toHex(ng)}${toHex(nb)}`;
  }, []);

  // 表示用リールアイテム生成をuseMemoでメモ化
  const reelItems = React.useMemo(() => {
    const items = [];
    // 基本フォント（必要に応じてテキストごとに自動縮小）
    const baseFontSize = gameCategory === 'sql' ? 14 : 20;
    const fontWeight = gameCategory === 'sql' ? 700 : 800;
    
    for (let i = 0; i < symbols.length + VISIBLE_ITEMS; i++) {
      const index = i % symbols.length;
      const symbol = symbols[index];
      const isHighlighted = highlightIndexes.includes(index);
      const baseColor = (isHighlighted && isBlueGlow) ? null : (symbol.color || '#ffffff');
      const displayColor = baseColor ? getReadableColor(baseColor) : undefined;
      const text = String(symbol?.text ?? '');
      const computedFontSize = unifiedFontSizePx != null ? unifiedFontSizePx : baseFontSize;
      items.push(
        <div
          key={i}
          className={`reel-item ${isHighlighted ? 'highlighted' : ''} ${isHighlighted && isBlueGlow ? 'reel-correct-active' : ''} ${isHighlighted && !isBlueGlow ? 'blink' : ''}`}
          style={{
            height: `${ITEM_HEIGHT}px`,
            lineHeight: `${ITEM_HEIGHT}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: `${computedFontSize}px`,
            fontWeight: fontWeight,
            color: displayColor,
            letterSpacing: '0.3px',
            transition: !spinning ? 'color 0.3s, text-shadow 0.3s, background 0.3s, opacity 0.2s' : 'none',
            boxSizing: 'border-box'
          }}
        >
          {text}
        </div>
      );
    }
    return items;
  }, [symbols, highlightIndexes, isBlueGlow, spinning, getReadableColor, gameCategory, unifiedFontSizePx]);

  // アニメ or 停止にかかわらず一貫した offset 計算
  let renderOffset;
  if (spinning) {
    renderOffset = offset; // 正規化回転
  } else if (targetIndex != null) {
    // targetIndex のシンボルを中央(2行目)に配置 → (targetIndex -1) 行を先頭に来す
    const basePos = ((targetIndex - 1 + symbols.length) % symbols.length) * ITEM_HEIGHT;
    renderOffset = -basePos; // スクロール上方向なのでマイナス
  } else {
    renderOffset = offset; // フォールバック
  }

  return (
    <div
      ref={reelRef}
      className={`reel-column ${spinning ? 'spinning' : ''}`}
      style={{
        position: 'relative',
        overflow: 'hidden',
        height: `${ITEM_HEIGHT * VISIBLE_ITEMS}px`,
        // 背景はCSSの .reel-column に委ねる（高コントラストのためダークに）
        backgroundColor: 'transparent',
        borderRadius: '8px',
        border: '1px solid rgba(255,255,255,0.15)'
      }}
    >
      <div
        style={{
          transform: `translate3d(0, ${renderOffset}px, 0)`,
          transition: 'none',
          willChange: 'transform'
        }}
      >
        {reelItems}
      </div>
    </div>
  );
};

export default SlotReel;